import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatTabChangeEvent, MatTabGroup } from '@angular/material/tabs';

import { SchoolActivity, SchoolActivityInstance } from 'app/models';
import { FeatureFlagService, FLAGS } from 'app/services/feature-flag.service';
import { BehaviorSubject } from 'rxjs';
import { customScheduleRepeatEndValidator, flexScheduleRepeatEndValidator, SCHEDULE_REPEAT_END_DATE_KEY } from '../activity-form-util';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'sp-activity-schedule-form',
	providers: [DatePipe],
	styles: [
		`
			::ng-deep .sp-mat-tab-group.activity-schedule-mat-tab-group .mat-tab-header {
				margin-bottom: 36px;
			}
		`,
	],
	template: `
		<div>
			<ng-container *ngIf="!customScheduleEnabled">
				<sp-activity-schedule-form-flex
					[editScheduleForm]="editScheduleForm"
					[flexPeriodId]="activity?.flex_period_id || preSelectedFlexPeriodId || null"
					[activityInstances]="activityInstances"></sp-activity-schedule-form-flex>
			</ng-container>

			<mat-tab-group
				*ngIf="customScheduleEnabled"
				#tabs
				class="sp-mat-tab-group activity-schedule-mat-tab-group"
				disableRipple
				[disablePagination]="true"
				[@.disabled]="true"
				[selectedIndex]="editScheduleForm.value.scheduleType === 'flex_period' ? 0 : 1"
				(selectedTabChange)="scheduleTypeTabChanged($event)">
				<mat-tab>
					<ng-template mat-tab-label>
						<div class="tw-animate-fadein-200 tab-label-wrapper tw-text-center right-border">
							<div class="tab-label">During Flex Period</div>
						</div>
					</ng-template>
					<sp-activity-schedule-form-flex
						[editScheduleForm]="editScheduleForm"
						[activity]="activity"
						[flexPeriodId]="activity?.flex_period_id || preSelectedFlexPeriodId || null"
						[activityInstances]="activityInstances"></sp-activity-schedule-form-flex>
				</mat-tab>

				<mat-tab>
					<ng-template mat-tab-label>
						<div class="tw-animate-fadein-200 tab-label-wrapper tw-text-center right-border">
							<div class="tab-label">Custom Date & Time</div>
						</div>
					</ng-template>
					<sp-activity-schedule-form-custom
						[editScheduleForm]="editScheduleForm"
						[activity]="activity"
						[activityInstances]="activityInstances"></sp-activity-schedule-form-custom>
				</mat-tab>
			</mat-tab-group>
		</div>
	`,
})
export class ActivityScheduleFormComponent implements OnInit {
	@ViewChild('tabs', { static: false }) tabs!: MatTabGroup;

	@Input() editScheduleForm!: UntypedFormGroup;
	@Input() activity: SchoolActivity | null = null;
	@Input() preSelectedFlexPeriodId: number | undefined;
	@Input() activityInstances: BehaviorSubject<SchoolActivityInstance[]> | null = null;

	customScheduleEnabled = false;

	constructor(private featureService: FeatureFlagService) {}

	ngOnInit(): void {
		this.customScheduleEnabled = this.featureService.isFeatureEnabledV2(FLAGS.CustomActivitySchedule);

		// Set up form validation
		this.editScheduleForm
			.get(SCHEDULE_REPEAT_END_DATE_KEY)
			?.setValidators([
				flexScheduleRepeatEndValidator(this.editScheduleForm, this.activityInstances?.value ?? []),
				customScheduleRepeatEndValidator(this.editScheduleForm),
			]);
		this.editScheduleForm.updateValueAndValidity();
	}

	scheduleTypeTabChanged($event: MatTabChangeEvent): void {
		switch ($event.index) {
			case 0:
				this.editScheduleForm.patchValue({ scheduleType: 'flex_period' });
				break;
			case 1:
				this.editScheduleForm.patchValue({ scheduleType: 'custom' });
				break;
		}
	}
}
